import { acceptHMRUpdate, defineStore } from "pinia";
import { ref } from "vue";
import { createDecisionTreeApi } from "#/decision_tree_viewer/api/decision-tree";
import { createAppointmentSlotApi } from "#/decision_tree_viewer/api/appointment-slot";

import { DecisionTree } from "#/decision_tree_viewer/models/decision-tree";
import { AppointmentSlot } from "#/decision_tree_viewer/models/appointment-slot";

const { find: findDecisionTree } = createDecisionTreeApi();
const { find: findAppointmentSlot } = createAppointmentSlotApi();

export const useDecisionTreeStore = defineStore("decisionTree", () => {
  const decisionTreeId = ref(null);
  const decisionTreeAttributes = ref(null);

  const appointmentSlotId = ref(null);
  const appointmentSlotAttributes = ref(null);

  const defaultPatientType = ref(null);

  const previewMode = ref(false);
  const debugMode = ref(false);

  const decisionTree = ref(null);

  const currentState = ref(null);

  async function load() {
    await loadTree();
    await loadSlot();

    setDefaultPatientType();
  }

  async function setAppointmentSlot(id) {
    appointmentSlotId.value = id;
    await loadSlot();
  }

  function start() {
    decisionTree.value.start();

    if (currentState.value) {
      decisionTree.value.setCurrentState(currentState.value);
    }
  }

  function reset(props) {
    decisionTreeId.value = props.decisionTreeId;
    decisionTreeAttributes.value = props.decisionTreeAttributes;
    appointmentSlotId.value = props.appointmentSlotId;
    appointmentSlotAttributes.value = props.appointmentSlotAttributes;
    defaultPatientType.value = props.defaultPatientType;
  }

  function setCurrentState(state) {
    currentState.value = state;
  }

  function enablePreviewMode() {
    previewMode.value = true;
  }

  function enableDebugMode() {
    debugMode.value = true;
  }

  // PRIVATE

  async function loadTree() {
    if (decisionTreeId.value) {
      decisionTree.value = await findDecisionTree(decisionTreeId.value);
    } else if (decisionTreeAttributes.value) {
      decisionTree.value = new DecisionTree(decisionTreeAttributes.value);
    }
  }

  async function loadSlot() {
    if (appointmentSlotId.value) {
      decisionTree.value.appointmentSlot = await findAppointmentSlot(appointmentSlotId.value);
    } else if (appointmentSlotAttributes.value) {
      decisionTree.value.appointmentSlot = new AppointmentSlot(appointmentSlotAttributes.value);
    }
  }

  function setDefaultPatientType() {
    if (defaultPatientType.value) {
      decisionTree.value.defaultPatientType = defaultPatientType.value;
    }
  }

  return {
    // state
    decisionTree,
    previewMode,
    debugMode,

    // actions
    load,
    setAppointmentSlot,
    start,
    reset,
    setCurrentState,
    enablePreviewMode,
    enableDebugMode,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDecisionTreeStore, import.meta.hot));
}
